import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} NotificationReceiver
 * @property {string} type - Receiver type (e.g. "user")
 * @property {number} id - Receiver ID
 * @property {string|null} name - Receiver name
 * @property {string} role_slug - Role slug (e.g. "school-admin")
 * @property {string|null} seen_at - Seen timestamp
 * @property {string|null} read_at - Read timestamp
 * @property {string|null} dismissed_at - Dismissed timestamp
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {object} GetNotificationUserListParams
 * @property {number} batch_id - Batch ID of the notification
 * @property {number} [page] - Page number for pagination
 * @property {number} [per_page] - Number of items per page
 */

/**
 * Custom hook to fetch receivers of a sent notification by batch ID
 */
export const useGetNotificationReceiverList = () => {
    const [receivers, setReceivers] = useState(
        /** @type {NotificationReceiver[] | null | undefined} */(undefined),
    );
    const [meta, setMeta] = useState(
        /** @type {PaginationMeta | null | undefined} */(undefined),
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const { axiosInstance } = useAxios();

    /**
     * Fetch notification receivers by batch ID
     * @async
     * @param {GetNotificationUserListParams} params
     * @returns {Promise<void>}
     */
    const getNotificationReceiverList = async ({
        batch_id,
        page = 1,
        per_page = ITEMS_PER_PAGE,
    }) => {
        try {
            setLoading(true);
            setError("");

            const query = new URLSearchParams();
            query.append("page", page);
            query.append("per_page", per_page);

            const url = `${TENANT_DOMAIN}/announcements/sent/${batch_id}/receivers?${query.toString()}`;

            const response = await axiosInstance.get(url);

            setReceivers(response.data?.data || []);
            setMeta(response.data?.meta || null);
        } catch (err) {
            setReceivers(null);
            setMeta(null);
            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        receivers,
        meta,
        loading,
        error,
        getNotificationReceiverList,
    };
};